import React, { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";

export function ForgotPasswordPage() {
  const [submitted, setSubmitted] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { setError("Please enter your email address."); return; }
    setLoading(true);
    setError(null);
    try {
      await new Promise((r) => setTimeout(r, 800));
      setSubmitted(true);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {/* Left dark panel */}
      <Box sx={{
        display: { xs: "none", md: "flex" },
        flexDirection: "column",
        justifyContent: "space-between",
        width: 460, flexShrink: 0,
        bgcolor: "#0F172A", p: 5,
        position: "relative", overflow: "hidden",
      }}>
        <Box sx={{ position: "absolute", top: -100, right: -100, width: 360, height: 360, borderRadius: "50%", background: "radial-gradient(circle, rgba(91,91,214,0.3) 0%, transparent 70%)", pointerEvents: "none" }} />
        <Box sx={{ position: "absolute", bottom: -80, left: -80, width: 280, height: 280, borderRadius: "50%", background: "radial-gradient(circle, rgba(14,165,233,0.18) 0%, transparent 70%)", pointerEvents: "none" }} />

        {/* Brand */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, position: "relative" }}>
          <Box sx={{ width: 36, height: 36, borderRadius: "10px", background: "linear-gradient(135deg, #818CF8 0%, #5B5BD6 100%)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 14px rgba(91,91,214,0.5)" }}>
            <BarChartOutlinedIcon sx={{ fontSize: 20, color: "#fff" }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#F1F5F9", lineHeight: 1.2 }}>OrkaAnalytics</Typography>
            <Typography sx={{ fontSize: "0.65rem", color: "#475569", letterSpacing: "0.04em" }}>Marketing platform</Typography>
          </Box>
        </Box>

        {/* Copy */}
        <Box sx={{ position: "relative" }}>
          <Typography sx={{ fontSize: "1.875rem", fontWeight: 800, color: "#F1F5F9", lineHeight: 1.25, letterSpacing: "-0.03em", mb: 2 }}>
            Locked out?{" "}
            <Box component="span" sx={{ background: "linear-gradient(90deg, #818CF8 0%, #38BDF8 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              We've got you.
            </Box>
          </Typography>
          <Typography sx={{ fontSize: "0.875rem", color: "#64748B", lineHeight: 1.75 }}>
            Enter your email and we'll send a reset link to get you back into your account right away.
          </Typography>
        </Box>

        {/* Bottom note */}
        <Box sx={{ bgcolor: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 3, p: 3 }}>
          <Typography sx={{ fontSize: "0.8rem", color: "#475569" }}>
            Remember your password?{" "}
            <Link component={RouterLink} to="/signin" sx={{ color: "#818CF8", textDecoration: "none", fontWeight: 600, "&:hover": { textDecoration: "underline" } }}>
              Sign in →
            </Link>
          </Typography>
        </Box>
      </Box>

      {/* Right form panel */}
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", bgcolor: "#F0F2F7", p: { xs: 3, sm: 5 } }}>
        {/* Mobile brand */}
        <Box sx={{ display: { xs: "flex", md: "none" }, alignItems: "center", gap: 1.5, mb: 4 }}>
          <Box sx={{ width: 32, height: 32, borderRadius: "9px", background: "linear-gradient(135deg, #818CF8 0%, #5B5BD6 100%)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <BarChartOutlinedIcon sx={{ fontSize: 18, color: "#fff" }} />
          </Box>
          <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#0F172A" }}>OrkaAnalytics</Typography>
        </Box>

        <Box sx={{ bgcolor: "#fff", borderRadius: 3, border: "1px solid #E8EDF5", p: { xs: 3, sm: 4.5 }, width: "100%", maxWidth: 420, boxShadow: "0px 4px 24px rgba(15,23,42,0.07)" }}>
          {submitted ? (
            <Box sx={{ textAlign: "center", py: 2 }}>
              <Box sx={{ width: 56, height: 56, borderRadius: "50%", bgcolor: "#F0FDF4", border: "1px solid #BBF7D0", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2.5 }}>
                <MarkEmailReadOutlinedIcon sx={{ fontSize: 28, color: "#10B981" }} />
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: "1.3rem", color: "#0F172A", mb: 1 }}>Check your inbox</Typography>
              <Typography variant="body2" sx={{ color: "#64748B", lineHeight: 1.75, mb: 3.5 }}>
                If <strong>{email}</strong> is linked to an account, you'll receive a reset link shortly. Check your spam folder if it doesn't arrive.
              </Typography>
              <Button fullWidth variant="outlined" component={RouterLink} to="/signin"
                startIcon={<ArrowBackIcon fontSize="small" />} sx={{ py: 1.125 }}>
                Back to sign in
              </Button>
            </Box>
          ) : (
            <>
              <Typography sx={{ fontWeight: 700, fontSize: "1.4rem", color: "#0F172A", mb: 0.75 }}>Reset your password</Typography>
              <Typography variant="body2" sx={{ color: "#64748B", mb: 3 }}>
                Enter the email on your account and we'll send a reset link.
              </Typography>

              {error && <Alert severity="error" sx={{ mb: 2.5 }} onClose={() => setError(null)}>{error}</Alert>}

              <form onSubmit={handleSubmit} noValidate>
                <TextField fullWidth name="email" label="Email address" type="email" margin="normal"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email" autoFocus disabled={loading} />
                <Button fullWidth type="submit" variant="contained" size="large" disabled={loading}
                  startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
                  sx={{ mt: 3, py: 1.25, fontSize: "0.9375rem" }}>
                  {loading ? "Sending…" : "Send reset link"}
                </Button>
              </form>

              <Button fullWidth variant="text" component={RouterLink} to="/signin"
                startIcon={<ArrowBackIcon fontSize="small" />}
                sx={{ mt: 2, color: "#64748B", "&:hover": { bgcolor: "#F8FAFC" } }}>
                Back to sign in
              </Button>
            </>
          )}
        </Box>

        <Typography variant="caption" sx={{ color: "#94A3B8", mt: 4 }}>
          © {new Date().getFullYear()} OrkaAnalytics · All rights reserved
        </Typography>
      </Box>
    </Box>
  );
}
